import React from 'react'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import styled from 'styled-components'
import { Redirect } from 'react-router-dom'
import shortid from 'shortid'
import { addRecentGame, getCachedGame, markRecentGameCompleted } from '../../utils/recentGames'
import GameService from '../../services/game'
import Crossword from '../crossword/crossword'
import { celebrate } from '../../utils/confetti'
import { COLORS } from '../../utils/colors'
import useLocalStorage from '../../hooks/useLocalStorage'
import ShareButton from './share-button'
import ShareModal from './share-modal'
import HomeButton from './home-button'
import useDuration from './use-duration'
import { trackEvent } from '../../analytics/client'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import './game.scss'

const SOCKET_URL = process.env.NODE_ENV === 'production' ? window.location.origin : process.env.API_URL
const OFFLINE_ID = -1
const RECONNECT_INTERVAL_MS = 3000
const PRESENCE_COLOR_MAP = {
  red: 'rgba(219, 94, 72, 0.92)',
  green: 'rgba(138, 219, 72, 0.92)',
  blue: 'rgba(72, 155, 219, 0.92)',
  purple: 'rgba(153, 72, 219, 0.92)',
  orange: 'rgba(219, 170, 72, 0.92)',
}
const resolveReportedColor = (color) => {
  if (!color || typeof color !== 'string') {
    return undefined
  }
  const normalized = color.trim().toLowerCase()
  return PRESENCE_COLOR_MAP[normalized] || color.trim()
}
const normalizeCompletionStats = (stats) => {
  if (!Array.isArray(stats)) {
    return []
  }
  return stats
    .map(stat => {
      const userToken = typeof stat?.userToken === 'string' ? stat.userToken : ''
      const nickname = (typeof stat?.nickname === 'string' ? stat.nickname : '').trim() || userToken
      return {
        userToken,
        nickname,
        letters: Number.isFinite(stat?.letters) ? stat.letters : 0,
        percent: Number.isFinite(stat?.percent) ? stat.percent : 0,
        color: typeof stat?.color === 'string' ? stat.color : undefined,
      }
    })
    .filter(stat => stat.userToken)
}
const isValidGamePayload = (game) => (
  Boolean(
    game
    && typeof game.short_id === 'string'
    && typeof game.board === 'string'
    && Number.isInteger(game.width)
    && Number.isInteger(game.height)
  )
)

const StyledShareButton = styled(ShareButton)`
    position: absolute;
    right: 8%;
    cursor: pointer;

    @media screen and (min-width: 1000px) {
        right: 25%;
    }
`

const StyledHomeButton = styled(HomeButton)`
    position: absolute;
    left: 8%;
    cursor: pointer;

    @media screen and (min-width: 1000px) {
        left: 25%;
    }
`

const Game = ({ match, history }) => {
  const [game, setGame] = useState({})
  const [users, setUsers] = useState({})
  const [me, setMe] = useState(undefined)
  const [won, setWon] = useState(false)
  const [gameSecondsOffset, setGameSecondsOffset] = useState(0)
  const [localStartTime, setLocalStartTime] = useState(undefined)
  const _userToken = localStorage.getItem('userToken')
  const duration = useDuration(localStartTime, game.duration_seconds, gameSecondsOffset, !won)
  const _crossword = useRef(null)
  const _socket = useRef(null)
  const [offline, setOffline] = useState(false)
  const [offlineUpdates, setOfflineUpdates] = useLocalStorage(`offline-${match.params.gameId}`, [])
  const [settings, setSettings] = useLocalStorage('settings', {}, 0)
  const [ready, setReady] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [gameOverStats, setGameOverStats] = useState([])
  const [showGameOverModal, setShowGameOverModal] = useState(false)
  const gameOverAutoOpenedRef = useRef(false)
  const receivedCompletionEventRef = useRef(false)
  const hasTrackedGameStartRef = useRef(false)
  const isLoadingGameRef = useRef(false)
  const trackedGameOpenedRef = useRef(new Set())

  const downClues = useMemo(() => {
    if (!game.down) {
      return {}
    }
    const clues = {}
    Object.keys(game.down).forEach(key => {
      const { row, col } = game.down[key]
      if (!clues[row]) {
        clues[row] = {}
      }
      clues[row][col] = key
    })
    return clues
  }, [game.down])

  const acrossClues = useMemo(() => {
    if (!game.across) {
      return {}
    }
    const clues = {}
    Object.keys(game.across).forEach(key => {
      const { row, col } = game.across[key]
      if (!clues[row]) {
        clues[row] = {}
      }
      clues[row][col] = key
    })
    return clues
  }, [game.across])

  const onClickTimer = () => {
    const nextTimerValue = !settings.timer
    setSettings(settings => ({
      ...settings,
      timer: nextTimerValue,
    }))
    trackEvent(ANALYTICS_EVENTS.SETTINGS_UPDATED, {
      setting: 'timer',
      value: nextTimerValue,
      gameId: match.params.gameId,
    })
  }

  useEffect(() => {
    if (!match.params.gameId) return
    gameOverAutoOpenedRef.current = false
    receivedCompletionEventRef.current = false
    hasTrackedGameStartRef.current = false
    trackedGameOpenedRef.current = new Set()
    setShowGameOverModal(false)
    setGameOverStats([])
    loadGame()
    return () => {
      if (_socket.current) {
        _socket.current.disconnect()
      }
    }
  }, [match.params.gameId])

  useEffect(() => {
    if (!won || gameOverAutoOpenedRef.current) {
      return
    }
    if (!receivedCompletionEventRef.current && gameOverStats.length === 0) {
      return
    }
    gameOverAutoOpenedRef.current = true
    setShowGameOverModal(true)
  }, [won, gameOverStats.length])

  useEffect(() => {
    setGameOverStats(normalizeCompletionStats(game?.completion_stats))
  }, [game?.completion_stats])

  const decoratedGameOverStats = useMemo(() => {
    const myResolvedColor = resolveReportedColor(me?.color)
    const reportedColors = Array.from(new Set(
      Object.values(users || {})
        .map(user => resolveReportedColor(user?.color))
        .filter(Boolean),
    ))
    const remainingColors = reportedColors.filter(color => color !== myResolvedColor)
    let nextColorIndex = 0

    const sortedStats = [...gameOverStats].sort((a, b) => {
      if (b.letters !== a.letters) {
        return b.letters - a.letters
      }
      return a.nickname.localeCompare(b.nickname)
    })

    return sortedStats.map((stat) => {
      const isCurrentUser = Boolean(_userToken && stat.userToken === _userToken)
      let playerColor = resolveReportedColor(stat.color)

      if (!playerColor && isCurrentUser) {
        playerColor = myResolvedColor
      }
      if (!playerColor && remainingColors.length > 0) {
        playerColor = remainingColors[nextColorIndex % remainingColors.length]
        nextColorIndex += 1
      }

      return {
        ...stat,
        isCurrentUser,
        playerColor,
      }
    })
  }, [gameOverStats, me?.color, users, _userToken])

  const myGameOverStat = useMemo(
    () => decoratedGameOverStats.find((stat) => stat.isCurrentUser),
    [decoratedGameOverStats],
  )

  useEffect(() => {
    if (game.completed_at || (game.board && game.board === game.solution)) {
        setWon(true)
    }
  }, [game.board, game.solution, game.completed_at])

  useEffect(() => {
    if (!won) {
        return
    }
    if (game.short_id) {
      markRecentGameCompleted(game.short_id)
    }
    celebrate()
  }, [won, game.short_id])

  useEffect(() => {
    if (!offlineUpdates.length) {
      return
    }
    
    if (!_socket.current) {
      return
    }

    // if connected, send up the wire
    processOfflineUpdates()
  }, [offlineUpdates.length, _socket.current])

  // effect to reconnect socket after disconnected
  useEffect(() => {
    if (!offline) {
      return
    }
    const intervalId = window.setInterval(loadGame, RECONNECT_INTERVAL_MS)
    return () => {
      if (intervalId) {
        window.clearInterval(intervalId)
      }
    }
  }, [offline])

  useEffect(() => {
    if (!_crossword.current) {
      return
    }
    if (!offlineUpdates.length) {
      return
    }
    if (!ready) {
      return
    }
    // draw locally
    const { board } = _crossword.current.state
    const newBoard = { ...board }
    offlineUpdates.forEach(({ row, col, value }) => {
      const val = value === '-' ? '' : value
      if (!newBoard[row]) {
        newBoard[row] = {}
      }
      newBoard[row][col] = val
    })
    _crossword.current.setState({ board: newBoard })
  }, [offlineUpdates.length, _crossword.current, ready])

  const getColor = (usedColors) => {
    const _baseRandomColor = () => {
      const idx = Math.floor(Math.random() * COLORS.length)
      return COLORS[idx]
    }
    const { color: preferredColor } = settings

    // if (window.localStorage && typeof window.localStorage.getItem === 'function' &&
    //     window.localStorage.getItem('myColor')) {
    //   return window.localStorage.getItem('myColor')
    // }

    // if theres no remaining colors, return preferred or random
    const remainingColors = COLORS.filter(c => !usedColors.has(c))

    if (!remainingColors.length) {
      return preferredColor || _baseRandomColor()
    }

    // if there is a preferred color, return it if it's available
    if (preferredColor && remainingColors.includes(preferredColor)) {
      return preferredColor
    }

    // otherwise, return a random remaining color
    const idx = Math.floor(Math.random() * remainingColors.length)
    return remainingColors[idx]
  }

  const setColor = (color) => {
    if (!window.localStorage || typeof window.localStorage.setItem !== 'function') {
      return
    }
    window.localStorage.setItem('myColor', color)
    setMe({ color })
    if (!_socket.current) {
      return
    }
    _socket.current.emit('setcolor', color)
  }

  const connectSocket = (game) => {
    const { short_id: shortId } = game
    if (_socket.current) {
      _socket.current.removeAllListeners()
      _socket.current.disconnect()
    }
    _socket.current = io(SOCKET_URL)
    _socket.current.on('connect', () => {
      _socket.current.emit('loadgame', { gameId: shortId, userToken: _userToken, platform: 'web' })
      if (!localStartTime) {
        setLocalStartTime(Date.now())
      }
      setOffline(false)
    })
    _socket.current.on('reconnect', () => {
      _socket.current.emit('loadgame', { gameId: shortId, userToken: _userToken, platform: 'web' })
      if (!localStartTime) {
        setLocalStartTime(Date.now())
      }
      setOffline(false)
    })
    _socket.current.on('disconnect', () => {
      setOffline(true)
    })
    _socket.current.on('users', users => {
      setUsers(users)
    })
    _socket.current.on('doneloading', users => {
      const color = getColor(new Set(Object.values(users).map(u => u.color)))
      setColor(color)
      _crossword.current.setInitialPosition()
      setReady(true)
    })
    _socket.current.on('boardupdate', ({ row, col, value, socketId }) => {
      if (_socket.id === socketId) {
        // we sent this update, no need to process again
        return
      }
      const { board } = _crossword.current.state
      const newBoard = { ...board }
      let val = value
      if (value === '-') {
        val = ''
      }
      newBoard[row][col] = val
      _crossword.current.setState({ board: newBoard })
      const boardStr = Object.values(newBoard)
        .flatMap(arr => arr.map(item => item === '' ? '.' : item))
        .join('')
      if (boardStr === game.solution) {
        setWon(true)
      }
      // drop from offline update
      setOfflineUpdates(offlineUpdates => 
        offlineUpdates.filter(({ row: offlineRow, col: offlineCol }) => (
          row !== parseInt(offlineRow) && col !== parseInt(offlineCol)
        )
      ))
    })
    _socket.current.on('boardupdates', updates => {
      const { board } = _crossword.current.state
      const newBoard = { ...board }
      updates.forEach(({ row, col, value }) => {
        let val = value
        if (value === '-') {
          val = ''
        }
        newBoard[row][col] = val
      })
      _crossword.current.setState({ board: newBoard })
      const boardStr = Object.values(newBoard)
        .flatMap(arr => arr.map(item => item === '' ? '.' : item))
        .join('')
      if (boardStr === game.solution) {
        setWon(true)
      }
    })
    _socket.current.on('gamecompleted', ({ board: completedBoard, completedAt, stats }) => {
      const normalizedStats = normalizeCompletionStats(stats)
      receivedCompletionEventRef.current = true
      if (_crossword.current && completedBoard && game.width && game.height) {
        const newBoard = []
        for (let i = 0; i < game.height; i += 1) {
          const row = []
          for (let j = 0; j < game.width; j += 1) {
            const item = completedBoard[(i * game.width) + j]
            row.push(item === '.' || item === '-' ? '' : item)
          }
          newBoard.push(row)
        }
        _crossword.current.setState({ board: newBoard })
      }
      setGameOverStats(normalizedStats)
      setGame(prev => ({
        ...prev,
        board: completedBoard || prev.board,
        completed_at: completedAt || prev.completed_at || new Date().toISOString(),
        completion_stats: normalizedStats,
      }))
      setWon(true)
      trackEvent(ANALYTICS_EVENTS.PUZZLE_COMPLETED, {
        gameId: shortId,
      })
    })
    _socket.current.on('sendpings', ({ row, col }) => {
      const { pingKeys } = _crossword.current.state
      const newPingKeys = [...pingKeys]
      newPingKeys[row][col] = shortid.generate()
      _crossword.current.setState({ pingKeys: newPingKeys })
    })
    _socket.current.on('game_seconds_offset', offset => {
      setGameSecondsOffset(offset)
    })
  }

  const loadGame = async () => {
    const { gameId } = match.params

    if (!gameId) {
      return
    }
    if (isLoadingGameRef.current) {
      return
    }
    isLoadingGameRef.current = true

    const trackGameOpenedOnce = ({ gameId, isOffline }) => {
      const key = `${gameId}:${isOffline ? 'offline' : 'online'}`
      if (trackedGameOpenedRef.current.has(key)) {
        return
      }
      trackedGameOpenedRef.current.add(key)
      trackEvent(ANALYTICS_EVENTS.GAME_OPENED, {
        gameId,
        offline: isOffline,
      })
    }

    try {
      // TODO: add timeout?
      const game = await GameService.getGameInfo(gameId)
      if (isValidGamePayload(game)) {
        setOffline(false)
        localStorage.setItem('lastGameId', gameId)
        addRecentGame(game)
        setGame(game)
        connectSocket(game)
        trackGameOpenedOnce({ gameId, isOffline: false })
      } else {
        throw new Error('not found')
      }
    } catch (err) {
      if (offline) {
        // already offline, dont reset state
        return
      }
      if (err.message === 'Failed to fetch') {
        const cachedGame = getCachedGame(gameId)
        if (cachedGame) {
          setOffline(true)
          setGame(cachedGame)
          setGameOverStats([])
          setUsers({
            [OFFLINE_ID]: {
              color: settings.color,
              row: 0,
              col: 0,
            }
          })
          setColor(settings.color)
          _crossword.current.setInitialPosition()
          setReady(true)
          trackGameOpenedOnce({ gameId, isOffline: true })
        }
      } else {
        console.error(`Error loading game: ${err.message}`)
        alert('Invalid game ID!')
        history.push('/game/continue')
      }
    } finally {
      isLoadingGameRef.current = false
    }
  }

  const processOfflineUpdates = () => {
    if (won || game.completed_at) {
      return
    }
    // step 1. consolidate
    const boardMap = {}
    offlineUpdates.forEach(({ row, col, value }) => {
      if (!boardMap[row]) {
        boardMap[row] = {}
      }
      boardMap[row][col] = value
    })

    // step 2. deliver
    // TODO: batch delivery
    const gameId = game.short_id
    Object.keys(boardMap).forEach(row => {
      Object.keys(boardMap[row]).forEach(col => {
        const value = boardMap[row][col]
        _socket.current.emit('updateboard', {
          gameId,
          row: parseInt(row),
          col: parseInt(col),
          value,
        })
      })
    })
  }

  const updateLetter = (row, col, val) => {
    if (won || game.completed_at) {
      return
    }
    const { gameId } = match.params
    let value = val
    if (!val || !val.trim()) {
      value = '-'
    }
    const { board } = _crossword.current.state
    // window.game = game
    // window.board = board
    const update = { gameId, row, col, value }
    if (!hasTrackedGameStartRef.current) {
      hasTrackedGameStartRef.current = true
      trackEvent(ANALYTICS_EVENTS.GAME_STARTED, {
        gameId,
      })
    }
    if (!_socket.current) {
      setOfflineUpdates(offlineUpdates => [
        ...offlineUpdates,
        update,
      ])
      return
    }
    // TODO: optimize this, still slow
    // TODO: dont need to reset state on boardUpdate when its the same user
    // backend can stop sending this to the updating client
    // TODO: removed, bc its already happening in crossword.js
    // const newBoard = { ...board }
    // newBoard[row][col] = value === '-' ? '' : value
    // _crossword.current.setState({ board: newBoard })

    _socket.current.emit('updateboard', { gameId, row, col, value })
    const boardStr = Object.values(board)
      .flatMap(arr => arr.map(item => item === '' ? '.' : item))
      .join('')
    if (boardStr === game.solution) {
      setWon(true)
    }
  }

  const updatePos = (row, col) => {
    const socketId = _socket.current?.id || OFFLINE_ID
    setUsers(users => ({
      ...users,
      [socketId]: {
        ...users[socketId],
        row,
        col,
      }
    }))
    if (!_socket.current) {
      return
    }
    _socket.current.emit('updatepos', { row, col })
  }

  const emitPing = (row, col) => {
    if (!_socket.current) {
      return
    }
    _socket.current.emit('emitping', { row, col })
    trackEvent(ANALYTICS_EVENTS.PING_SENT, { gameId: game.short_id, row, col })
  }

  const { gameId } = match.params
  const statusString = game.short_id ? game.title.trim() || 'Untitled' : 'Loading...'

  if (!gameId) {
    return <Redirect to='/' />
  }

  // console.log('users: ', users)
  // console.log('socket id: ', _socket.current?.id)

  return (
    <div className='GameWindow'>
      <h1>{statusString}</h1>
      <div className='Row'>
        <StyledHomeButton />
        <div className='Col'>
          <span>Game ID: {gameId}</span>
          <span className='Duration' onClick={onClickTimer} style={{ cursor: 'pointer' }}>
            &nbsp;&nbsp;&nbsp;Timer:&nbsp;
            {settings.timer 
              ? <span style={{ fontSize: '1em' }}>{duration}</span>
              : <span style={{ fontSize: '1em', fontStyle: 'italic' }}>hidden</span>}
          </span>
          {!offline && (
            <span className='UserString'>
              Players: {Object.keys(users).length}&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          )}
          {offline && (
            <span className='UserString'>
              <strong>⚠️ OFFLINE ⚠️</strong> 
            </span>
          )}
        </div>
        <StyledShareButton onClick={() => setShowShareModal(true)} />
      </div>
      {showShareModal && (
        <ShareModal gameId={gameId} onClose={() => setShowShareModal(false)} />
      )}
      {showGameOverModal && (
        <div className='GameOverOverlay'>
          <div className='GameOverModal'>
            <h2>Puzzle Completed!</h2>
            <p className='GameOverDuration'>Duration: {duration}</p>
            {gameOverStats.length > 0 ? (
              <div className='GameOverStatsCard'>
                {myGameOverStat && (
                  <div className='GameOverMeSummary'>
                    You placed {myGameOverStat.letters} letters ({myGameOverStat.percent}%)
                  </div>
                )}
                <table className='GameOverStatsTable'>
                  <colgroup>
                    <col style={{ width: '40%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '42%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Letters</th>
                      <th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {decoratedGameOverStats.map((stat) => (
                      <tr key={stat.userToken} className={stat.isCurrentUser ? 'GameOverRowMe' : ''}>
                        <td>
                          <span className='GameOverPlayerNameCell'>
                            <span className='GameOverPlayerDot' style={stat.playerColor ? { backgroundColor: stat.playerColor } : undefined} />
                            <span className='GameOverPlayerName'>{stat.isCurrentUser ? 'Me' : stat.nickname}</span>
                            {stat.isCurrentUser && <span className='GameOverMeBadge'>YOU</span>}
                          </span>
                        </td>
                        <td>{stat.letters}</td>
                        <td>
                          <div className='GameOverShareCell'>
                            <span>{stat.percent}%</span>
                            <div className='GameOverShareTrack'>
                              <div
                                className='GameOverShareFill'
                                style={{
                                  width: `${Math.max(0, Math.min(100, stat.percent))}%`,
                                  ...(stat.playerColor ? { backgroundColor: stat.playerColor } : {}),
                                }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Finalizing score...</p>
            )}
            <div className='GameOverActions'>
              <button className='GameOverCloseButton' type='button' onClick={() => setShowGameOverModal(false)}>
                Close
              </button>
              <button className='GameOverNextButton' type='button' onClick={() => history.push('/game/new')}>
                Next Puzzle
              </button>
            </div>
          </div>
        </div>
      )}
      <Crossword
        ref={_crossword}
        {...game}
        isComplete={won || Boolean(game.completed_at)}
        showCheck={!(won || game.completed_at)}
        onViewStats={gameOverStats.length > 0 ? () => setShowGameOverModal(true) : undefined}
        clues={{ down: downClues, across: acrossClues }}
        users={users}
        me={me}
        updateLetter={updateLetter}
        updatePos={updatePos}
        emitPing={emitPing}
        settings={settings} />
    </div>
  )
}

export default Game
